import React, { useState, useEffect } from "react";
import { IconButton, TextField, Box, CircularProgress, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { Remove, Add, ShoppingCart } from "@mui/icons-material";
import RemoveShoppingCartIcon from "@mui/icons-material/RemoveShoppingCart";
import Toast from "../../components/Toast";

const QuantityControl = ({
  isLogin, isStockAvailable, isCheckStock, isStockFractional, stockLimit,
  qty, onAddToCart, onDeleteToCart, increase, decrease,
  setQty, loading = false, isInCart = false,
}) => {
  console.log("llega al componente ",isStockFractional)
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [localQty, setLocalQty] = useState(qty);

  useEffect(() => { setLocalQty(qty); }, [qty]);

  let allowSell = false;
  if (isLogin) { allowSell = !isCheckStock ? isStockAvailable : true; }

  const maxQty = isCheckStock ? 99999 : stockLimit;

  const handleClickShopIcon = () => {
    if (!isCheckStock && !isStockAvailable) { Toast.info("No hay stock disponible."); return; }
    if (loading || !allowSell) return;
    isInCart ? onDeleteToCart?.() : onAddToCart?.(parseFloat(localQty) || 1);
  };

  // ── Escribir libremente ──
  const handleChange = (e) => {
    const value = e.target.value;

    if (isStockFractional) {
      // Acepta dígitos + un punto o coma + hasta 2 decimales
      const normalized = value.replace(',', '.');
      if (normalized === '' || normalized === '.' || /^\d*\.?\d{0,2}$/.test(normalized)) {
        setLocalQty(normalized === '' ? '' : normalized);
      }
    } else {
      // Solo enteros
      const raw = value.replace(/\D/g, "");
      if (raw === "") { setLocalQty(""); return; }
      const num = Math.min(Number(raw), maxQty);
      setLocalQty(num);
    }
  };

  // ── Validar al salir ──
  const handleBlur = () => {
    let final = parseFloat(localQty) || (isStockFractional ? 0.01 : 1);
    if (isStockFractional) {
      if (final < 0.01) final = 0.01;
      final = Math.round(final * 100) / 100; // max 2 decimales
    } else {
      if (final < 1) final = 1;
      final = Math.floor(final);
    }
    if (final > maxQty) final = maxQty;
    setLocalQty(final);

    if (isInCart && final !== qty && setQty) {
      setQty(final);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") e.target.blur();
  };

  const tooltip = !isLogin
    ? "Debes iniciar sesión"
    : !isCheckStock && !isStockAvailable
      ? "Sin stock disponible"
      : "";

  const disabled = loading || !allowSell;

  const CartBtn = () => (
    <IconButton onClick={handleClickShopIcon} disabled={disabled} size="small"
      sx={{
        width: 36, height: 36, borderRadius: 'var(--radius-md)',
        backgroundColor: isInCart ? 'var(--danger-bg)' : allowSell ? 'var(--accent)' : 'var(--bg-subtle)',
        border: isInCart ? '1.5px solid var(--danger)' : allowSell ? 'none' : '1.5px solid var(--border)',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: isInCart ? 'var(--danger)' : allowSell ? 'var(--accent-hover)' : 'var(--bg-subtle)',
          '& svg': { color: isInCart ? '#fff' : undefined },
        },
        '&:disabled': { backgroundColor: 'var(--bg-subtle)', border: '1.5px solid var(--border)' },
      }}>
      {loading
        ? <CircularProgress size={16} sx={{ color: 'var(--text-muted)' }} />
        : isInCart
          ? <RemoveShoppingCartIcon sx={{ fontSize: 18, color: 'var(--danger)' }} />
          : <ShoppingCart sx={{ fontSize: 18, color: allowSell ? '#fff' : 'var(--text-muted)' }} />
      }
    </IconButton>
  );

  return (
    <Box sx={{
      display: "flex", alignItems: "center", justifyContent: "center",
      gap: 0.1, padding: '1px',
      backgroundColor: 'var(--bg-subtle)', borderRadius: 'var(--radius-md)',
      border: '1px solid var(--border)',
    }}>
      {/* Botón - */}
      <IconButton size="small" onClick={decrease}
        disabled={(isStockFractional ? qty <= 0.01 : qty <= 1) || loading || !isInCart}
        sx={{
          width: 28, height: 28, borderRadius: 'var(--radius-sm)',
          backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)',
          transition: 'all 0.15s ease',
          '&:hover': { backgroundColor: 'var(--bg-subtle)', borderColor: 'var(--border-hover)' },
        }}>
        <Remove sx={{ fontSize: 14 }} />
      </IconButton>

      {/* Input editable */}
      <TextField
        value={localQty}
        size="small"
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={loading}
        onClick={(e) => e.target.select()}
        inputProps={{
          style: {
            textAlign: "center",
            fontFamily: 'var(--font-mono)',
            fontWeight: 700,
            fontSize: '0.82rem',
          },
          inputMode: isStockFractional ? "decimal" : "numeric",
          pattern: isStockFractional ? "[0-9]*[.,]?[0-9]*" : "[0-9]*",
        }}
        sx={{
          width: Math.max(48, String(localQty).length * 10 + 28),
          minWidth: 48,
          maxWidth: 90,
          transition: 'width 0.15s ease',
          '& .MuiOutlinedInput-root': {
            borderRadius: 'var(--radius-sm)',
            backgroundColor: 'var(--bg-card)',
            height: 28,
            transition: 'all 0.2s ease',
            '& fieldset': { borderColor: 'var(--border)' },
            '&:hover fieldset': { borderColor: 'var(--border-hover)' },
            '&.Mui-focused fieldset': { borderColor: 'var(--accent)', borderWidth: 2 },
            '&.Mui-focused': { backgroundColor: '#fff' },
          },
        }}
      />

      {/* Botón + */}
      <IconButton size="small" onClick={increase}
        disabled={loading || !isInCart || qty >= maxQty}
        sx={{
          width: 28, height: 28, borderRadius: 'var(--radius-sm)',
          backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)',
          transition: 'all 0.15s ease',
          '&:hover': { backgroundColor: 'var(--bg-subtle)', borderColor: 'var(--border-hover)' },
        }}>
        <Add sx={{ fontSize: 14 }} />
      </IconButton>

      {/* Botón carrito */}
      {tooltip
        ? <Tooltip title={tooltip}><span><CartBtn /></span></Tooltip>
        : <CartBtn />
      }
    </Box>
  );
};

export default QuantityControl;